import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import { Download, CardGiftcard, Receipt } from '@mui/icons-material';
import { API_BASE_URL } from '../../config/api.config';

interface MyVoucher {
  id: number;
  code: string;
  originalAmount: number;
  remainingAmount: number;
  status: string;
  paymentStatus: string;
  validUntil: string | null;
  createdAt: string;
  recipientName: string | null;
  canDownloadPdf: boolean;
}

interface VoucherInvoice {
  id: number;
  invoiceNumber: string;
  voucherCode: string;
  voucherId: number;
  invoiceDate: string;
  amount: number;
  paymentStatus: string;
}

interface BookingInvoice {
  id: number;
  invoiceNumber: string;
  bookingNumber: string;
  bookingId: number;
  invoiceDate: string;
  totalAmount?: number;
  totalGross?: number;
  status: string;
  isPaid?: boolean;
  isFullyPaid?: boolean;
}

const statusLabels: Record<string, string> = {
  'PendingPayment': 'Zahlung ausstehend',
  'Active': 'Aktiv',
  'PartiallyRedeemed': 'Teilweise eingelöst',
  'FullyRedeemed': 'Vollständig eingelöst',
  'Expired': 'Abgelaufen',
  'Cancelled': 'Storniert'
};

const statusColors: Record<string, 'warning' | 'success' | 'info' | 'default' | 'error'> = {
  'PendingPayment': 'warning',
  'Active': 'success',
  'PartiallyRedeemed': 'info',
  'FullyRedeemed': 'default',
  'Expired': 'error',
  'Cancelled': 'error'
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const DocumentList: React.FC = () => {
  const [vouchers, setVouchers] = useState<MyVoucher[]>([]);
  const [invoices, setInvoices] = useState<VoucherInvoice[]>([]);
  const [bookingInvoices, setBookingInvoices] = useState<BookingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMyVouchers();
    fetchMyInvoices();
    fetchBookingInvoices();
  }, []);

  const fetchMyVouchers = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/vouchers/my-vouchers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Bitte melden Sie sich erneut an.');
          return;
        }
        throw new Error('Fehler beim Laden der Gutscheine');
      }

      const data = await response.json();
      setVouchers(data);
    } catch (err: any) {
      setError(err.message || 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyInvoices = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/vouchers/my-invoices`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInvoices(data);
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
    }
  };

  const fetchBookingInvoices = async () => {
    try {
      const token = localStorage.getItem('authToken');

      // Load bookings first
      const bookingsResponse = await fetch(`${API_BASE_URL}/user/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (bookingsResponse.ok) {
        const bookingsData = await bookingsResponse.json();
        let bookingsList: any[] = [];
        if (Array.isArray(bookingsData)) {
          bookingsList = bookingsData;
        } else if (bookingsData.items) {
          bookingsList = bookingsData.items;
        }

        // Load invoices for each booking
        const allInvoices: BookingInvoice[] = [];
        for (const booking of bookingsList) {
          try {
            const invoiceResponse = await fetch(`${API_BASE_URL}/invoices/booking/${booking.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (invoiceResponse.ok) {
              const invoiceData = await invoiceResponse.json();
              if (Array.isArray(invoiceData)) {
                allInvoices.push(...invoiceData.map((inv: any) => ({
                  ...inv,
                  bookingNumber: booking.bookingNumber,
                  bookingId: booking.id
                })));
              }
            }
          } catch {
            // Invoice not found for this booking, skip
          }
        }
        setBookingInvoices(allInvoices);
      }
    } catch (err) {
      console.error('Error fetching booking invoices:', err);
    }
  };

  const handleDownloadVoucherPdf = async (voucherId: number, voucherCode: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/vouchers/${voucherId}/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('PDF konnte nicht heruntergeladen werden');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Gutschein-${voucherCode}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert(err.message || 'Fehler beim Download');
    }
  };

  const handleDownloadInvoicePdf = async (voucherId: number, invoiceNumber: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/vouchers/${voucherId}/invoice/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Rechnung konnte nicht heruntergeladen werden');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert(err.message || 'Fehler beim Download');
    }
  };

  const handleDownloadBookingInvoicePdf = async (invoiceId: number, invoiceNumber: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Rechnung konnte nicht heruntergeladen werden');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert(err.message || 'Fehler beim Download');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Receipt color="primary" />
        Meine Dokumente
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Gutscheine Section */}
      <Paper elevation={2} sx={{ mb: 3, borderRadius: '12px', overflow: 'hidden' }}>
        <Box sx={{
          p: 2,
          backgroundColor: '#910F0F',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <CardGiftcard />
          <Typography variant="h6">Meine gekauften Gutscheine</Typography>
        </Box>

        {vouchers.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Sie haben noch keine Gutscheine gekauft.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Kaufen Sie einen Gutschein und verschenken Sie unvergessliche Floßfahrten!
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Gutschein-Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Wert</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Restwert</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Gültig bis</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Kaufdatum</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">PDF</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vouchers.map((voucher) => (
                  <TableRow key={voucher.id} hover>
                    <TableCell>
                      <Typography
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          color: '#910F0F',
                          letterSpacing: '0.5px'
                        }}
                      >
                        {voucher.code}
                      </Typography>
                      {voucher.recipientName && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Für: {voucher.recipientName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatCurrency(voucher.originalAmount)}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color: voucher.remainingAmount < voucher.originalAmount ? '#f59e0b' : 'inherit',
                          fontWeight: voucher.remainingAmount < voucher.originalAmount ? 600 : 'normal'
                        }}
                      >
                        {formatCurrency(voucher.remainingAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={statusLabels[voucher.status] || voucher.status}
                        color={statusColors[voucher.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(voucher.validUntil)}</TableCell>
                    <TableCell>{formatDate(voucher.createdAt)}</TableCell>
                    <TableCell align="center">
                      {voucher.canDownloadPdf ? (
                        <Tooltip title="Gutschein-PDF herunterladen">
                          <IconButton
                            color="primary"
                            onClick={() => handleDownloadVoucherPdf(voucher.id, voucher.code)}
                            sx={{
                              backgroundColor: '#F5F0E6',
                              '&:hover': { backgroundColor: '#E7E1D1' }
                            }}
                          >
                            <Download />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="PDF verfügbar nach Zahlungseingang">
                          <span>
                            <IconButton disabled>
                              <Download />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Rechnungen Section */}
      {(invoices.length > 0 || bookingInvoices.length > 0) && (
        <Paper elevation={2} sx={{ mb: 3, borderRadius: '12px', overflow: 'hidden' }}>
          <Box sx={{
            p: 2,
            backgroundColor: '#910F0F',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Receipt />
            <Typography variant="h6">Rechnungen</Typography>
          </Box>

          {/* Buchungsrechnungen */}
          {bookingInvoices.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ px: 2, pt: 2, pb: 1, fontWeight: 600, color: '#666' }}>
                Buchungsrechnungen
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Rechnungsnummer</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Buchung</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Datum</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Betrag</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">PDF</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bookingInvoices.map((invoice) => {
                      const totalAmount = invoice.totalAmount ?? invoice.totalGross ?? 0;
                      const isPaid = invoice.isFullyPaid ?? invoice.isPaid ?? false;
                      return (
                        <TableRow key={`booking-${invoice.id}`} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            {invoice.invoiceNumber}
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontFamily: 'monospace', color: '#1976d2' }}>
                              {invoice.bookingNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                          <TableCell>{formatCurrency(totalAmount)}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={isPaid ? 'Bezahlt' : (invoice.status === 'Created' ? 'Erstellt' : invoice.status)}
                              color={isPaid ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Rechnung herunterladen">
                              <IconButton
                                color="primary"
                                onClick={() => handleDownloadBookingInvoicePdf(invoice.id, invoice.invoiceNumber)}
                                sx={{ backgroundColor: '#F5F0E6', '&:hover': { backgroundColor: '#E7E1D1' } }}
                              >
                                <Download />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Gutschein-Rechnungen */}
          {invoices.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ px: 2, pt: 2, pb: 1, fontWeight: 600, color: '#666' }}>
                Gutschein-Rechnungen
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Rechnungsnummer</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Gutschein</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Datum</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Betrag</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">PDF</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={`voucher-${invoice.id}`} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {invoice.invoiceNumber}
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontFamily: 'monospace', color: '#910F0F' }}>
                            {invoice.voucherCode}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                        <TableCell>{formatCurrency(invoice.amount)}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={invoice.paymentStatus === 'Paid' ? 'Bezahlt' : 'Zahlung ausstehend'}
                            color={invoice.paymentStatus === 'Paid' ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Rechnung herunterladen">
                            <IconButton
                              color="primary"
                              onClick={() => handleDownloadInvoicePdf(invoice.voucherId, invoice.invoiceNumber)}
                              sx={{ backgroundColor: '#F5F0E6', '&:hover': { backgroundColor: '#E7E1D1' } }}
                            >
                              <Download />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      )}

      {/* Future: Tickets Section */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: '12px', backgroundColor: '#f8fafc' }}>
        <Typography variant="h6" gutterBottom color="text.secondary">
          Tickets
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Weitere Dokumente wie Buchungstickets werden hier bald verfügbar sein.
        </Typography>
      </Paper>
    </Box>
  );
};

export default DocumentList;
